import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Activity,
  Bell,
  Shield,
  Zap,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  Loader2,
  Eye,
  EyeOff,
  Radio,
  Wifi,
  WifiOff,
  TrendingUp,
  FileWarning,
  ShieldCheck,
  XCircle,
  ChevronRight
} from 'lucide-react';
import api from '../lib/api';

export default function ContinuousMonitoringPage() {
  const navigate = useNavigate();
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [filter, setFilter] = useState({ status: '', severity: '', alert_type: '' });

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchData = async () => {
    try {
      const [statusRes, alertsRes] = await Promise.all([
        api.get('/vulnerabilities/monitoring/status'),
        api.get('/vulnerabilities/monitoring/alerts', { params: filter })
      ]);
      setMonitoringStatus(statusRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const runManualCheck = async () => {
    setChecking(true);
    try {
      const response = await api.post('/vulnerabilities/monitoring/check-now');
      toast.success(`Monitoring check complete! ${response.data.total_alerts} new alerts`);
      fetchData();
    } catch (error) {
      toast.error('Failed to run monitoring check');
    } finally {
      setChecking(false);
    }
  };

  const updateAlertStatus = async (alertId, status) => {
    try {
      await api.put(`/vulnerabilities/monitoring/alerts/${alertId}/status?status=${status}`);
      toast.success('Alert status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white'
    };
    return colors[severity] || 'bg-slate-400 text-white';
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'zero_day': return <Zap className="w-5 h-5 text-red-500" />;
      case 'patch_available': return <ShieldCheck className="w-5 h-5 text-green-500" />;
      case 'threat_detected': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default: return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-red-100 text-red-700 border-red-200',
      acknowledged: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      investigating: 'bg-blue-100 text-blue-700 border-blue-200',
      resolved: 'bg-green-100 text-green-700 border-green-200',
      dismissed: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return styles[status] || styles.new;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="continuous-monitoring">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Continuous Monitoring</h1>
            <p className="text-slate-500 mt-1">Real-time threat detection and patch monitoring</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={runManualCheck} disabled={checking} data-testid="check-now-btn">
              {checking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <Radio className="w-4 h-4 mr-2" />
                  Check Now
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Monitoring Status */}
        {monitoringStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className={`border-l-4 ${monitoringStatus.is_active ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{monitoringStatus.is_active ? 'Active' : 'Inactive'}</p>
                    <p className="text-xs text-slate-500">Status</p>
                  </div>
                  {monitoringStatus.is_active ? (
                    <Wifi className="w-6 h-6 text-green-500" />
                  ) : (
                    <WifiOff className="w-6 h-6 text-red-500" />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{monitoringStatus.active_feeds}</p>
                    <p className="text-xs text-slate-500">Active Feeds</p>
                  </div>
                  <Activity className="w-6 h-6 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{monitoringStatus.total_alerts_today}</p>
                    <p className="text-xs text-slate-500">Alerts Today</p>
                  </div>
                  <Bell className="w-6 h-6 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-red-600">{monitoringStatus.critical_alerts}</p>
                    <p className="text-xs text-slate-500">Critical</p>
                  </div>
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{monitoringStatus.zero_days_tracked}</p>
                    <p className="text-xs text-slate-500">Zero-Days</p>
                  </div>
                  <Zap className="w-6 h-6 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{monitoringStatus.patches_pending}</p>
                    <p className="text-xs text-slate-500">Patches</p>
                  </div>
                  <ShieldCheck className="w-6 h-6 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Threat Feeds */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Active Threat Feeds</CardTitle>
            <CardDescription>Real-time intelligence sources being monitored</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <div className="p-2 bg-green-100 rounded-full">
                  <Zap className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Zero-Day Feed</p>
                  <p className="text-xs text-green-600">Active • Last check: 5m ago</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="p-2 bg-blue-100 rounded-full">
                  <ShieldCheck className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Patch Monitor</p>
                  <p className="text-xs text-blue-600">Active • Last check: 10m ago</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <div className="p-2 bg-orange-100 rounded-full">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Threat Intel</p>
                  <p className="text-xs text-orange-600">Active • Last check: 15m ago</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="p-2 bg-purple-100 rounded-full">
                  <FileWarning className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">CVE Database</p>
                  <p className="text-xs text-purple-600">Active • Last check: 30m ago</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Filter Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>

              <select
                value={filter.severity}
                onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
              </select>

              <select
                value={filter.alert_type}
                onChange={(e) => setFilter({ ...filter, alert_type: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Types</option>
                <option value="zero_day">Zero-Day</option>
                <option value="patch_available">Patch Available</option>
                <option value="threat_detected">Threat Detected</option>
              </select>

              {(filter.status || filter.severity || filter.alert_type) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFilter({ status: '', severity: '', alert_type: '' })}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Threat Alerts</CardTitle>
              <Badge variant="outline">{alerts.length} alerts</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <p className="font-medium">No alerts found</p>
                <p className="text-sm mt-1">Your systems are looking secure</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id}
                    className="flex items-start justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {getAlertTypeIcon(alert.alert_type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{alert.title}</h3>
                          {alert.cve_ids?.length > 0 && (
                            <Badge variant="outline" className="text-xs">{alert.cve_ids[0]}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{alert.description}</p>
                        {alert.affected_systems && (
                          <p className="text-xs text-slate-500 mt-2">
                            <strong>Affected:</strong> {alert.affected_systems}
                          </p>
                        )}
                        {alert.recommended_action && (
                          <p className="text-xs text-blue-600 mt-1">
                            <strong>Action:</strong> {alert.recommended_action}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(alert.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                      <Badge variant="outline" className={getStatusBadge(alert.status)}>
                        {alert.status}
                      </Badge>
                      {alert.status === 'new' && (
                        <div className="flex gap-1 mt-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateAlertStatus(alert.id, 'acknowledged')}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Ack
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateAlertStatus(alert.id, 'dismissed')}
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Dismiss
                          </Button>
                        </div>
                      )}
                      {alert.status === 'acknowledged' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateAlertStatus(alert.id, 'resolved')}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/vulnerabilities')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Vulnerability Dashboard</h3>
                    <p className="text-sm text-slate-500">View all vulnerabilities</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/vulnerability-scanner')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Run New Scan</h3>
                    <p className="text-sm text-slate-500">Scan assets for vulnerabilities</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
